export type SelectedCells = { [key: number]: [number[], number[]] }

export function selectionFromRange(
  row: number,
  col: number,
  row2: number,
  col2: number
): [number[], number[]] {
  if (col2 === -1 || row2 === -1) {
    return [[row], [col]]
  }

  const firstRow = Math.min(row, row2)
  const firstCol = Math.min(col, col2)
  return [
    Array.from({ length: Math.abs(row2 - row) + 1 }, (_, index) => firstRow + index),
    Array.from({ length: Math.abs(col2 - col) + 1 }, (_, index) => firstCol + index)
  ]
}

export function getVisibleRowIndices(rowsMetaTags: string[], activeRowTags: string[]) {
  if (activeRowTags.length === 0) {
    return rowsMetaTags.map((_, index) => index)
  }

  return rowsMetaTags
    .map((tag, index) => (activeRowTags.includes(tag) ? index : null))
    .filter((index): index is number => index != null)
}

export function areAllRowsSelected(selections: SelectedCells, rowCount: number) {
  if (!selections || rowCount === 0) return false

  const selectedRows = new Set(
    Object.values(selections).flatMap(([rows, columns]) =>
      columns.some(column => column >= 0) ? rows : []
    )
  )
  return Array.from({ length: rowCount }, (_, index) => index).every(index =>
    selectedRows.has(index)
  )
}

/** Keep selections on the same source rows when filtering the visible row tags. */
export function remapSelectionsForRowTags(
  selections: SelectedCells,
  rowsMetaTags: string[],
  previousActiveRowTags: string[],
  nextActiveRowTags: string[],
  preserveAllRows = false
): SelectedCells {
  if (!selections) {
    return null
  }

  const previousVisibleRows = getVisibleRowIndices(rowsMetaTags, previousActiveRowTags)
  const nextVisibleRows = getVisibleRowIndices(rowsMetaTags, nextActiveRowTags)

  return Object.entries(selections).reduce((remappedSelections, [key, [rows, columns]]) => {
    const remappedRows = preserveAllRows
      ? nextVisibleRows.map((_, index) => index)
      : rows
          .map(row => previousVisibleRows[row])
          .filter((row): row is number => row != null)
          .map(row => nextVisibleRows.indexOf(row))
          .filter(row => row >= 0)

    if (remappedRows.length > 0) {
      remappedSelections[Number(key)] = [remappedRows, columns]
    }

    return remappedSelections
  }, {} as SelectedCells)
}
